import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { adminAPI } from "@/services/api";
import { formatCurrency, formatDate } from "@/utils/constants";
import { getErrorMessage } from "@/utils/errorHelpers";
import { ChevronLeft, ChevronRight, Search, UserRound } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 15;
const SORT_OPTIONS = [
  { value: "newest", label: "Plus recents" },
  { value: "oldest", label: "Plus anciens" },
  { value: "name", label: "Nom A a Z" },
  { value: "role", label: "Role" },
  { value: "verified", label: "KYC verifie en premier" },
];

const ROLE_LABELS = { admin: "Administrateur", partner: "Partenaire", buyer: "Acheteur" };

export default function AdminUsersDirectoryPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Impossible de charger les utilisateurs"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const rows = users.filter((currentUser) => {
      const matchesRole = role === "all" || currentUser.role === role;
      const haystack = [currentUser.id, currentUser.full_name, currentUser.email, currentUser.telephone, currentUser.parrain_id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesRole && (!normalizedQuery || haystack.includes(normalizedQuery));
    });

    return [...rows].sort((left, right) => {
      if (sort === "name") return (left.full_name || "").localeCompare(right.full_name || "", "fr");
      if (sort === "role") return (ROLE_LABELS[left.role] || left.role || "").localeCompare(ROLE_LABELS[right.role] || right.role || "", "fr");
      if (sort === "verified") return Number(Boolean(right.is_verified)) - Number(Boolean(left.is_verified)) || String(right.created_at || "").localeCompare(String(left.created_at || ""));
      const order = String(right.created_at || "").localeCompare(String(left.created_at || ""));
      return sort === "oldest" ? -order : order;
    });
  }, [query, role, sort, users]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const visibleUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [query, role, sort]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const openConsole = async (userId) => {
    setProfileLoading(true);
    setProfile(null);
    try {
      const response = await adminAPI.getUserProfile(userId);
      setProfile(response.data || null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Impossible d'ouvrir la console utilisateur"));
    } finally {
      setProfileLoading(false);
    }
  };

  if (loading) return <LoadingState label="Chargement des utilisateurs..." />;

  return (
    <PageShell data-testid="admin-users-directory-page">
      <PageHeader title="Utilisateurs" description="Liste globale des comptes MMJ. Cliquez sur une ligne pour ouvrir sa console." />

      <Card className="border-[#E6E0D6] shadow-sm">
        <CardContent className="grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_220px_220px]">
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5C625F]" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} className="pl-9" placeholder="Rechercher par nom, ID, email, telephone ou parrain" />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger><SelectValue placeholder="Tous les roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les roles</SelectItem>
              <SelectItem value="admin">Administrateurs</SelectItem>
              <SelectItem value="partner">Partenaires</SelectItem>
              <SelectItem value="buyer">Acheteurs</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger><SelectValue placeholder="Trier" /></SelectTrigger>
            <SelectContent>{SORT_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-[#E6E0D6] shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg text-[#2A4B46]">{filteredUsers.length} utilisateur(s)</CardTitle>
          <Pagination page={page} pageCount={pageCount} total={filteredUsers.length} setPage={setPage} />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border border-[#E6E0D6]">
            <table className="w-full min-w-[980px] text-left text-sm">
              <thead className="bg-[#F3EFE9] text-xs font-semibold uppercase tracking-[0.08em] text-[#5C625F]">
                <tr><th className="px-4 py-3">Utilisateur</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Parrain</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Inscription</th></tr>
              </thead>
              <tbody>
                {visibleUsers.map((currentUser) => (
                  <tr
                    key={currentUser.id}
                    className="cursor-pointer border-t border-[#E6E0D6] transition-colors hover:bg-[#FBFAF7] focus-visible:bg-[#FBFAF7]"
                    tabIndex={0}
                    role="button"
                    onClick={() => openConsole(currentUser.id)}
                    onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") openConsole(currentUser.id); }}
                    aria-label={`Ouvrir la console de ${currentUser.full_name}`}
                  >
                    <td className="px-4 py-3"><p className="font-semibold text-[#2A4B46]">{currentUser.full_name || "-"}</p><p className="font-mono text-xs text-[#5C625F]">{currentUser.id}</p></td>
                    <td className="px-4 py-3"><Badge className="bg-[#F3EFE9] text-[#2A4B46]">{ROLE_LABELS[currentUser.role] || currentUser.role || "-"}</Badge></td>
                    <td className="px-4 py-3"><p>{currentUser.email}</p><p className="text-xs text-[#5C625F]">{currentUser.telephone || "-"}</p></td>
                    <td className="px-4 py-3 font-mono text-xs">{currentUser.parrain_id || "-"}</td>
                    <td className="px-4 py-3"><p className="text-xs">KYC : {currentUser.is_verified ? "Verifie" : "Non verifie"}</p><p className="text-xs text-[#5C625F]">Email : {currentUser.email_verified ? "Verifie" : "Non verifie"}</p></td>
                    <td className="px-4 py-3 text-xs text-[#5C625F]">{formatDate(currentUser.created_at)}</td>
                  </tr>
                ))}
                {visibleUsers.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-[#5C625F]">Aucun utilisateur ne correspond a cette recherche.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-4"><Pagination page={page} pageCount={pageCount} total={filteredUsers.length} setPage={setPage} /></div>
        </CardContent>
      </Card>

      <UserConsoleDialog profile={profile} loading={profileLoading} close={() => { setProfile(null); setProfileLoading(false); }} />
    </PageShell>
  );
}

function Pagination({ page, pageCount, total, setPage }) {
  const start = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(total, page * PAGE_SIZE);
  return (
    <div className="flex items-center gap-2 text-xs text-[#5C625F]">
      <span className="hidden sm:inline">{start}-{end} sur {total}</span>
      <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft size={14} /></Button>
      <span className="min-w-10 text-center font-semibold text-[#2A4B46]">{page}/{pageCount}</span>
      <Button type="button" variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))}><ChevronRight size={14} /></Button>
    </div>
  );
}

function UserConsoleDialog({ profile, loading, close }) {
  const account = profile?.user;
  const identity = profile?.kyc?.identity || {};
  const address = identity.adresse || {};
  const transactions = Array.isArray(profile?.transactions) ? profile.transactions : [];
  const orders = Array.isArray(profile?.orders) ? profile.orders : [];
  const products = Array.isArray(profile?.products) ? profile.products : [];
  const auditLogs = Array.isArray(profile?.audit_logs) ? profile.audit_logs : [];

  return (
    <Dialog open={Boolean(profile) || loading} onOpenChange={(open) => !open && close()}>
      <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-5xl sm:rounded-2xl">
        <DialogHeader><DialogTitle>Console utilisateur{account ? ` - ${account.full_name}` : ""}</DialogTitle></DialogHeader>
        {loading || !profile ? <LoadingState label="Ouverture de la console..." /> : (
          <Tabs defaultValue="profile" className="space-y-4">
            <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-[#F3EFE9] p-1">
              <TabsTrigger value="profile"><UserRound size={14} className="mr-1" /> Profil</TabsTrigger>
              <TabsTrigger value="activity">Activite</TabsTrigger>
              <TabsTrigger value="audit">Audit</TabsTrigger>
            </TabsList>
            <TabsContent value="profile" className="space-y-5 text-sm">
              <div className="grid gap-5 lg:grid-cols-2">
                <InfoBlock title="Compte" rows={[
                  ["ID", account?.id], ["Role", ROLE_LABELS[account?.role] || account?.role], ["Email", account?.email], ["Telephone", account?.telephone], ["Pays", account?.pays], ["Inscription", formatDate(account?.created_at)],
                ]} />
                <InfoBlock title="Statut" rows={[
                  ["Email", account?.email_verified ? "Verifie" : "Non verifie"], ["KYC", account?.is_verified ? "Verifie" : "Non verifie"], ["PAE", account?.is_active_pae ? "Actif" : "Inactif"], ["PAR", account?.is_active_par ? "Actif" : "Inactif"], ["Parrain", account?.parrain_id], ["Niveau", account?.partner_level],
                ]} />
              </div>
              {account?.role === "partner" && <InfoBlock title="Points partenaire" rows={[
                ["Points monnaie", formatCurrency(account?.points_monnaie || 0)], ["Points activation", `${account?.points_activation || 0} MC`], ["Points equipe", `${account?.points_equipe || 0} MC`], ["Points ascension", `${account?.points_ascension || 0} MC`],
              ]} />}
              {profile.kyc && <InfoBlock title="Identite KYC" rows={[
                ["Nom", [identity.nom, identity.postnom, identity.prenom].filter(Boolean).join(" ")], ["Sexe", identity.sexe], ["Naissance", identity.date_naissance], ["Piece d'identite", identity.numero_identite], ["Adresse", [address.rue, address.numéro, address.commune, address.ville, address.province, address.pays].filter(Boolean).join(", ")], ["Validation", formatDate(profile.kyc.validated_at)],
              ]} />}
            </TabsContent>
            <TabsContent value="activity" className="grid gap-4 lg:grid-cols-3">
              <ConsoleList title={`Commandes (${orders.length})`} rows={orders} render={(row) => `${row.product_title || row.product_id || "Produit"} - ${formatCurrency(row.price_mc || row.amount || 0)}`} />
              <ConsoleList title={`Transactions (${transactions.length})`} rows={transactions} render={(row) => `${row.type || row.bonus_type || "Transaction"} - ${formatCurrency(row.amount || row.amount_mc || 0)}`} />
              <ConsoleList title={`Produits (${products.length})`} rows={products} render={(row) => `${row.title || "Produit"} - ${row.status || "-"}`} />
            </TabsContent>
            <TabsContent value="audit"><ConsoleList title={`Journal d'audit (${auditLogs.length})`} rows={auditLogs} render={(row) => `${row.action || "Action"} - ${row.target_type || ""} ${row.target_id || ""}`} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InfoBlock({ title, rows }) {
  return <div className="rounded-xl border border-[#E6E0D6] p-4"><h3 className="mb-3 font-semibold text-[#2A4B46]">{title}</h3><div className="space-y-2">{rows.map(([label, value]) => <div key={label} className="grid grid-cols-[145px_1fr] gap-3"><span className="text-xs text-[#5C625F]">{label}</span><span className="break-words">{value || "-"}</span></div>)}</div></div>;
}

function ConsoleList({ title, rows, render }) {
  return <div className="rounded-xl border border-[#E6E0D6] p-4"><h3 className="mb-3 font-semibold text-[#2A4B46]">{title}</h3><div className="max-h-96 space-y-2 overflow-y-auto">{rows.length === 0 ? <p className="text-sm text-[#5C625F]">Aucune donnee.</p> : rows.map((row, index) => <div key={row.id || row.transaction_id || index} className="rounded-lg bg-[#FBFAF7] p-3 text-xs"><p>{render(row)}</p><p className="mt-1 text-[#5C625F]">{formatDate(row.created_at || row.validated_at)}</p></div>)}</div></div>;
}
